import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AttributePropertyTags from 'components/quote_builder/attributePropertyTags.jsx';

// A custom replacement for a material/machine price <select> on /mcc. A native <option> can hold only
// plain text, so it cannot show the linked record's property tags; this dropdown renders each option as a
// label line plus a row of those tags -- the SAME AttributePropertyTags chips the settings list and the
// library picker show for that record (a material type's printing methods + capabilities, or a machine's
// type / colour count / sheet size / thickness), driven by `listingProperties`. Only the pickers that opt
// in (config.option_tags_attribute_key) use it; every other unit-price picker keeps its native <select>.
//
// `groups` is the option model already built by UserUnitPrice.jsx: [{ key, label?, options: [{ key, value,
// label, selectedLabel?, disabled?, tagsProperties? }] }] -- the same grouping the native <select> rendered
// (empty / not-available / my unit prices / most commonly used / add-new). The open list shows `label`; the
// collapsed button shows `selectedLabel` when the option supplies one (own prices use it to drop the price
// tail, already shown below the picker), else `label`. Selecting an option calls onSelect(value);
// the caller routes a system price into the copy-from-system flow and 'new-attribute' into the create
// popup, exactly as the native <select>'s onChange (setValueWrapped) did.
const UserUnitPriceTagSelect = ({ groups, value, onSelect, disabled, notAvailable, subFormAttributes, listingProperties, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef();
  const buttonRef = useRef();
  const panelRef = useRef();

  const allOptions = groups.reduce((collected, group) => collected.concat(group.options), []);
  const selectableOptions = allOptions.filter((option) => !option.disabled);
  const selectedOption = allOptions.find((option) => String(option.value) === String(value));
  const selectedSelectableIndex = selectableOptions.findIndex((option) => String(option.value) === String(value));

  // Close when the mouse goes down outside the widget, the standard dropdown behaviour. Only while open,
  // so a closed picker adds no document listener.
  useEffect(() => {
    if (!open) { return; }
    const onDocumentMouseDown = (event) => {
      // The panel is portaled to document.body (below), so a click on an option is NOT inside containerRef.
      // A mousedown inside either the control or the portaled panel counts as inside; otherwise choosing an
      // option would close the panel on mousedown and unmount the option before its onClick could fire.
      const insideControl = containerRef.current && containerRef.current.contains(event.target);
      const insidePanel = panelRef.current && panelRef.current.contains(event.target);
      if (!insideControl && !insidePanel) { setOpen(false); }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [open]);

  // The panel is portaled to document.body and positioned fixed (viewport coordinates), anchored to the
  // trigger button's on-screen rectangle -- the same escape-the-clipping-ancestor approach as HoverTooltip.
  // Portaling is required because /mcc wraps every section body in an overflow:hidden box
  // (FrankQuoteBuilderPage) that would otherwise clip an in-flow, absolutely-positioned panel.
  const positionPanel = useCallback(() => {
    if (!buttonRef.current) { return; }
    const rect = buttonRef.current.getBoundingClientRect();
    // Keep a min-width panel from spilling past the right edge when the control sits near the viewport edge.
    const estimatedWidth = Math.max(rect.width, 360);
    const left = Math.min(rect.left, window.innerWidth - estimatedWidth - 8);
    setPanelPosition({ top: rect.bottom + 2, left: Math.max(8, left), width: rect.width });
  }, []);

  // Re-anchor to the button whenever the page scrolls or resizes while the panel is open; fixed coordinates
  // do not follow the button on their own. Capture phase so a scroll in any ancestor scroll container counts.
  useEffect(() => {
    if (!open) { return; }
    window.addEventListener('scroll', positionPanel, true);
    window.addEventListener('resize', positionPanel);
    return () => {
      window.removeEventListener('scroll', positionPanel, true);
      window.removeEventListener('resize', positionPanel);
    };
  }, [open, positionPanel]);

  const choose = (option) => {
    if (option.disabled) { return; }
    // A native <select> fires no onChange when the current option is re-picked; match that, so re-selecting
    // the already-selected price does not stamp updated_at and trigger a spurious re-price / publish.
    if (String(option.value) !== String(value)) { onSelect(option.value); }
    setOpen(false);
  };

  const openPanel = () => {
    positionPanel();
    setActiveIndex(selectedSelectableIndex >= 0 ? selectedSelectableIndex : 0);
    setOpen(true);
  };

  const onButtonKeyDown = (event) => {
    if (disabled) { return; }
    if (event.key === 'Escape') { setOpen(false); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) { openPanel(); return; }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        const next = current + step;
        if (next < 0) { return 0; }
        if (next > selectableOptions.length - 1) { return selectableOptions.length - 1; }
        return next;
      });
      return;
    }
    if ((event.key === 'Enter' || event.key === ' ') && open && activeIndex >= 0 && activeIndex < selectableOptions.length) {
      event.preventDefault();
      choose(selectableOptions[activeIndex]);
    }
  };

  const buttonLabel = () => {
    if (selectedOption) {
      // The collapsed value prefers a shorter selectedLabel when the option supplies one -- own prices drop
      // the price tail here because it is already shown in the summary line below the picker -- and falls
      // back to the full option label otherwise.
      const collapsed = (selectedOption.selectedLabel != undefined && String(selectedOption.selectedLabel).trim() !== '')
        ? selectedOption.selectedLabel
        : selectedOption.label;
      if (String(collapsed).trim() !== '') { return collapsed; }
    }
    return placeholder || '';
  };

  const renderOption = (option, selectableIndex, striped) => {
    const isSelected = String(option.value) === String(value) && option.value !== undefined && option.value !== '';
    const isActive = selectableIndex >= 0 && selectableIndex === activeIndex;
    const classNames = ['user-unit-price-tag-select__option'];
    if (option.disabled) { classNames.push('user-unit-price-tag-select__option--disabled'); }
    if (option.placeholder) { classNames.push('user-unit-price-tag-select__option--placeholder'); }
    if (striped) { classNames.push('user-unit-price-tag-select__option--stripe'); }
    if (isSelected) { classNames.push('user-unit-price-tag-select__option--selected'); }
    if (isActive) { classNames.push('user-unit-price-tag-select__option--active'); }

    return (
      <div
        key={option.key}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled || undefined}
        className={classNames.join(' ')}
        onClick={() => choose(option)}
        onMouseEnter={() => { if (!option.disabled) { setActiveIndex(selectableIndex); } }}
      >
        <div className="user-unit-price-tag-select__option-label">
          {isSelected && <i className="fa fa-check user-unit-price-tag-select__check" aria-hidden="true"></i>}
          <span>{option.label}</span>
        </div>
        {option.tagsProperties && subFormAttributes && (
          <AttributePropertyTags
            properties={option.tagsProperties}
            subFormAttributes={subFormAttributes}
            listingProperties={listingProperties}
          />
        )}
      </div>
    );
  };

  // A running index over only the selectable options, so keyboard highlight and mouse-enter skip group
  // headers and disabled (filtered-out) rows. Reset each render; the whole list re-renders together.
  let selectableCursor = -1;
  // A separate running index over the striped rows (real price rows, not the placeholder or disabled
  // ones) so alternate rows get a faint background -- the tall two-tag-row options need it to read as
  // distinct rows. It runs across groups so the zebra pattern is continuous down the whole list.
  let stripeCursor = -1;

  return (
    <div
      ref={containerRef}
      className={`user-unit-price-tag-select ${notAvailable ? 'disabledForm' : ''} ${disabled ? 'user-unit-price-tag-select--disabled' : ''}`}
    >
      <button
        ref={buttonRef}
        type="button"
        className="form-control user-unit-price-tag-select__button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { if (!disabled) { open ? setOpen(false) : openPanel(); } }}
        onKeyDown={onButtonKeyDown}
      >
        <span className="user-unit-price-tag-select__button-label">{buttonLabel()}</span>
        <i className="fa fa-caret-down user-unit-price-tag-select__caret" aria-hidden="true"></i>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="user-unit-price-tag-select__panel"
          role="listbox"
          style={{ top: panelPosition.top, left: panelPosition.left, width: panelPosition.width }}
        >
          {groups.map((group) => (
            group.options.length > 0 && (
              <div key={group.key} className="user-unit-price-tag-select__group">
                {group.label !== undefined && group.label !== '' && (
                  <div className="user-unit-price-tag-select__group-label">{group.label}</div>
                )}
                {group.options.map((option) => {
                  if (!option.disabled) { selectableCursor += 1; }
                  let striped = false;
                  if (!option.placeholder && !option.disabled) {
                    stripeCursor += 1;
                    striped = stripeCursor % 2 === 1;
                  }
                  return renderOption(option, option.disabled ? -1 : selectableCursor, striped);
                })}
              </div>
            )
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserUnitPriceTagSelect;
